import { useSelector } from '@/api/state';
import { actions as usersActions } from '@/api/users';
import { getFormServiceError } from '@/helpers/formHelper';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Field,
  Fieldset,
  Form,
  Modal,
  RawInput,
  Select,
} from '@payaca/ambrosia';
import { User } from '@payaca/types/userTypes';
import {
  FC,
  ReactNode,
  useEffect,
  useRef,
  useState,
  type JSX,
  type RefObject,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { z } from 'zod/v4';
import { useUserSystemRoleOptions } from '../../../hooks/useUserSystemRoleOptions';

const addUserFormSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().trim().min(1, 'Last name is required'),
  email: z
    .string()
    .trim()
    .min(1, 'Email address is required')
    .email('This field must contain a valid email address'),
  systemRole: z.string().min(1, 'System role is required'),
});

type TFormState = z.infer<typeof addUserFormSchema>;

const findUserByEmail = (users: User[], email: string) => {
  const normalised = email.trim().toLowerCase();
  return users.find((user) => (user.email ?? '').toLowerCase() === normalised);
};

const isRemovedUser = (user: User) => !!user.deactivatedAt && !user.inviteToken;

// A user already on the account blocks a fresh invite, unless they were removed
// — in which case they are reinstated instead. Reads the latest users via a ref
// so validation stays reactive as the redux list loads.
const buildSchema = (usersRef: RefObject<User[]>) =>
  addUserFormSchema.superRefine((data, ctx) => {
    const existingUser = findUserByEmail(usersRef.current, data.email);
    if (!existingUser || isRemovedUser(existingUser)) {
      return;
    }
    ctx.addIssue({
      code: 'custom',
      path: ['email'],
      message: existingUser.deactivatedAt
        ? 'This user has already been invited.'
        : 'This user is already active.',
    });
  });

type Props = {
  /** Optional banner rendered above the fields (e.g. remaining user seats). */
  seatsInfo?: ReactNode;
  onAddUserSuccess?: () => void;
  onClose: () => void;
};

const AddUserForm: FC<Props> = ({
  seatsInfo,
  onAddUserSuccess,
  onClose,
}): JSX.Element => {
  const dispatch = useDispatch();
  const accountUsers = useSelector(
    (state: any) => state.users.accountUsers as User[]
  );
  const { options: userSystemRoleOptions } = useUserSystemRoleOptions();

  const [isProcessing, setIsProcessing] = useState(false);
  const [serviceError, setServiceError] = useState<string | null>(null);

  const accountUsersRef = useRef(accountUsers);
  accountUsersRef.current = accountUsers;

  const formMethods = useForm<TFormState>({
    resolver: zodResolver(buildSchema(accountUsersRef)),
    mode: 'onTouched',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      systemRole: '',
    },
  });

  useEffect(() => {
    dispatch(usersActions.getAccountUsers());
  }, [dispatch]);

  // Default the role to the first available option once the roles have loaded.
  useEffect(() => {
    if (userSystemRoleOptions.length && !formMethods.getValues('systemRole')) {
      formMethods.setValue('systemRole', userSystemRoleOptions[0].value);
    }
  }, [userSystemRoleOptions, formMethods]);

  const emailValue = formMethods.watch('email');
  const removedUser = emailValue
    ? findUserByEmail(accountUsers, emailValue)
    : undefined;
  const userHasBeenRemoved = !!removedUser && isRemovedUser(removedUser);

  // Reinstating an existing user doesn't need the name fields, so it runs
  // directly rather than through the invite form's validation.
  const reinstateUser = () => {
    if (!removedUser) {
      return;
    }
    setIsProcessing(true);
    setServiceError(null);
    dispatch(
      usersActions.reactivateUser(removedUser.id, (error?: string) => {
        setIsProcessing(false);
        if (!error) {
          onAddUserSuccess?.();
        } else {
          setServiceError('Something went wrong reinstating user');
        }
        dispatch(usersActions.getAccountUsers());
      })
    );
  };

  const onSubmit = (state: TFormState) => {
    if (userHasBeenRemoved) {
      reinstateUser();
      return;
    }

    setIsProcessing(true);
    setServiceError(null);
    dispatch(
      usersActions.inviteUserToAccount(
        {
          email: state.email,
          firstName: state.firstName,
          lastName: state.lastName,
          systemRole: state.systemRole,
        },
        (error?: string) => {
          setIsProcessing(false);
          if (!error) {
            onAddUserSuccess?.();
          } else {
            setServiceError(getFormServiceError('addUser', error));
          }
          dispatch(usersActions.getAccountUsers());
        }
      )
    );
  };

  return (
    <Form methods={formMethods} onSubmit={onSubmit}>
      <Modal.Body>
        {seatsInfo}
        <Fieldset>
          <Field name="firstName">
            <Field.Label>First name</Field.Label>
            <RawInput {...formMethods.register('firstName')} />
          </Field>
          <Field name="lastName">
            <Field.Label>Last name</Field.Label>
            <RawInput {...formMethods.register('lastName')} />
          </Field>
          <Field name="email">
            <Field.Label>Email address</Field.Label>
            <RawInput type="email" {...formMethods.register('email')} />
          </Field>
          <Controller<TFormState, 'systemRole'>
            name="systemRole"
            render={({ field: { value, onChange } }) => (
              <Field name="systemRole">
                <Field.Label>System role</Field.Label>
                <Select<string, false>
                  multiple={false}
                  value={value}
                  onChange={(role) => onChange(role)}
                  options={userSystemRoleOptions}
                  placeholder="Select a role"
                />
              </Field>
            )}
          />
        </Fieldset>
        {userHasBeenRemoved && (
          <Alert variant="soft" colour="yellow" className="mt-4">
            A user with the email address {emailValue} used to exist but has
            been removed. Reinstate them to restore their access.
          </Alert>
        )}
        {serviceError && (
          <Alert variant="soft" colour="red" className="mt-4">
            {serviceError}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          {userHasBeenRemoved ? (
            <Button
              type="button"
              isProcessing={isProcessing}
              disabled={isProcessing}
              onClick={reinstateUser}
            >
              Reinstate user
            </Button>
          ) : (
            <Button
              type="submit"
              isProcessing={isProcessing}
              disabled={isProcessing}
            >
              Send invite
            </Button>
          )}
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Form>
  );
};

export default AddUserForm;
